import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import api, { refreshSession } from "../lib/api";
import { isNative, syncPushToken, deactivatePushInstallation } from "../native";

const AuthCtx = createContext(null);

export const ROLE_HOME = {
  family_owner: "/app/family",
  family_member: "/app/family",
  senior: "/app/senior",
  care_manager: "/app/cm",
  ops_executive: "/app/admin",
  ops_manager: "/app/admin",
  admin: "/app/admin",
  super_admin: "/app/admin",
};

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/auth/me");
        setUser(r.data);
      } catch {
        try {
          await refreshSession();
          const r = await api.get("/auth/me");
          setUser(r.data);
        } catch { setUser(null); }
      } finally { setLoading(false); }
    })();
  }, []);

  const login = useCallback(async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    if (data.user) setUser(data.user);
    return data;
  }, []);

  // An authenticated session on a native install (re)associates the device installation
  // with this account — covers fresh logins, MFA completion and silent session refreshes.
  useEffect(() => {
    if (user && isNative()) syncPushToken();
  }, [user?.id]);

  const logout = useCallback(async () => {
    if (isNative()) {
      try { await deactivatePushInstallation(); } catch { /* best effort; next login re-associates */ }
    }
    try { await api.post("/auth/logout"); } catch { /* session already gone */ }
    setUser(null);
  }, []);

  return <AuthCtx.Provider value={{ user, loading, login, logout }}>{children}</AuthCtx.Provider>;
}

export function useAuth() {
  return useContext(AuthCtx);
}
